import '../css/ProjectOverview.less';

import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {Dropdown, Image, Modal} from 'semantic-ui-react';

import modalImg from '../assets/submit_benchmark_run.png';
import EditableField from './EditableField';
import {ProjectData} from '../state/graphql/projectPageQuery';
import {AccessOptions} from '../util/permissions';
import {TimeDelta} from '../util/time';
import {benchmarkProject} from '../util/urls';
import LegacyWBIcon from './elements/LegacyWBIcon';
import {ExtraProjectActions} from './ExtraProjectActions';
import ProjectAccess from './ProjectAccess';
import ProjectRenameModal from './ProjectRenameModal';
import makeComp from '../util/profiler';

interface ProjectOverviewProps {
  project: ProjectData;
  updateProject: any;
  deleteProject(): void;
  undeleteRuns(): void;
}

const ProjectOverview: React.FC<ProjectOverviewProps> = makeComp(
  ({project, updateProject, deleteProject, undeleteRuns}) => {
    const [showRename, setShowRename] = useState<boolean>(false);

    return (
      <div className="project-overview">
        <div className="project-overview-top">
          <div className="mini-page-header">
            <div className="name-header-wrapper">
              <EditableField
                showEditIcon
                className="name-header"
                asHeader="h1"
                placeholder={project.name}
                value={project.name}
                overrideClick={() => setShowRename(true)}
              />
              <ProjectRenameModal
                open={showRename}
                targetProject={{
                  name: project.name,
                  entityName: project.entityName,
                }}
                onClose={() => setShowRename(false)}
              />
              <ExtraProjectActions
                project={project}
                deleteProject={deleteProject}
                undeleteRuns={undeleteRuns}
              />
            </div>

            <EditableField
              className="description"
              readOnly={project.readOnly}
              multiline
              showEditIcon
              placeholder={'What problem does this project tackle?'}
              value={project.description}
              save={value => {
                updateProject({
                  variables: {id: project.id, description: value},
                });
              }}
            />
          </div>
          {project.linkedBenchmark && (
            <div className="overview-item">
              <div className="overview-key">Benchmark</div>
              <div className="overview-value">
                <Dropdown
                  className="dropdown-menu-icon-button benchmark-dropdown"
                  simple
                  icon={null}
                  trigger={
                    <>
                      <LegacyWBIcon name="benchmark" />
                      {'Linked to ' + project.linkedBenchmark.name}
                      <LegacyWBIcon name="chevron-expanded" />
                    </>
                  }>
                  <Dropdown.Menu>
                    <Dropdown.Item>
                      <Modal
                        trigger={<Dropdown.Item>Submit run</Dropdown.Item>}>
                        <Modal.Header icon="benchmark">
                          Submit to benchmark
                        </Modal.Header>
                        <Modal.Content>
                          <p>
                            To submit a run from the table, open the menu and
                            click <b>Submit to benchmark</b>.
                          </p>
                          <img
                            alt="Screenshot showing the submission dropdown is located in the runs table to the left of the name"
                            style={{
                              width: '100%',
                              marginLeft: 'auto',
                              marginRight: 'auto',
                            }}
                            src={modalImg}
                          />
                        </Modal.Content>
                      </Modal>
                    </Dropdown.Item>
                    <Dropdown.Item
                      href={benchmarkProject(project.linkedBenchmark)}>
                      Visit benchmark
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          )}
          <div className="overview-item">
            <div className="overview-key">Privacy</div>
            <div className="overview-value">
              <ProjectAccess
                project={project}
                updateProjectAccess={(access: AccessOptions) =>
                  updateProject({
                    variables: {
                      id: project.id,
                      access,
                    },
                  })
                }
              />
            </div>
          </div>
          {project.lastActive != null && (
            <div className="overview-item">
              <div className="overview-key">Last active</div>
              <div className="overview-value">
                {new Date(project.lastActive).toLocaleString()}
              </div>
            </div>
          )}
          <div className="overview-item">
            <div className="overview-key">Author</div>
            <div className="overview-value">
              <Link className="user-link" to={`/${project.user.username}`}>
                <Image
                  src={project.user.photoUrl}
                  avatar
                  onError={(i: any) => (i.target.style.display = 'none')}
                />
                {project.user.username}
              </Link>
            </div>
          </div>
          <div className="overview-item">
            <div className="overview-key">Contributors</div>
            <div className="overview-value">{`${project.totalUsers} user${
              project.totalUsers === 1 ? '' : 's'
            }`}</div>
          </div>
          <div className="overview-item">
            <div className="overview-key">Total runs</div>
            <div className="overview-value">{project.totalRuns}</div>
          </div>
          <div className="overview-item">
            <div className="overview-key">Total compute</div>
            <div className="overview-value">
              {new TimeDelta(project.totalRunTime).toSingleUnitString()}
            </div>
          </div>
        </div>
      </div>
    );
  },
  {id: 'ProjectOverview'}
);

export default ProjectOverview;
